import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { AdCard } from "@/components/AdCard";
import { Chip } from "@/components/Chip";

const TABS = [["pending", "Waiting for review"], ["approved", "Approved"], ["rejected", "Rejected"], ["all", "All"]];

export default function AdminAds() {
  const [status, setStatus] = useState("pending");
  const [ads, setAds] = useState(null);
  const [notes, setNotes] = useState({});
  const load = () => api.get("/admin/ads", { params: { status } }).then((r) => setAds(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status]);

  const act = async (ad, action) => {
    try {
      await api.post(`/admin/ads/${ad.id}/${action}`, { note: notes[ad.id] || "" });
      toast.success(action === "approve" ? "Approved. It's live for nearby plans." : "Rejected. The business will see your note.");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-10 lg:py-14 space-y-8" data-testid="admin-ads-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#23603F]">Admin</p>
        <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight">Ad review</h1>
        <p className="mt-2 text-stone-600 max-w-2xl">Nothing goes live without approval. Check that the offer is honest, the address is right, and the time slots make sense.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {TABS.map(([v, l]) => <Chip key={v} tone="forest" testId={`admin-ads-tab-${v}`} active={status === v} onClick={() => setStatus(v)}>{l}</Chip>)}
      </div>
      {ads?.length === 0 && <p className="text-stone-500" data-testid="admin-ads-empty">Nothing here right now.</p>}
      <div className="grid md:grid-cols-2 gap-4">
        {ads?.map((ad, i) => (
          <AdCard key={ad.id} ad={ad} testId={`admin-ad-${i}`}
            footer={ad.status === "pending" ? (
              <div className="pt-2 border-t border-stone-100 space-y-2">
                <input data-testid={`admin-ad-note-${i}`} value={notes[ad.id] || ""} onChange={(e) => setNotes({ ...notes, [ad.id]: e.target.value })} placeholder="Note to the business (required to reject)"
                  className="w-full h-9 rounded-lg border border-stone-300 px-3 text-sm" />
                <div className="flex gap-2">
                  <button data-testid={`admin-ad-approve-${i}`} onClick={() => act(ad, "approve")} className="flex-1 flex items-center justify-center gap-1 bg-[#2F7D55] hover:bg-[#23603F] text-white text-sm font-semibold py-2 rounded-lg"><Check className="w-4 h-4" /> Approve</button>
                  <button data-testid={`admin-ad-reject-${i}`} onClick={() => act(ad, "reject")} className="flex-1 flex items-center justify-center gap-1 border border-stone-300 hover:bg-red-50 hover:text-red-700 text-sm font-semibold py-2 rounded-lg"><X className="w-4 h-4" /> Reject</button>
                </div>
              </div>
            ) : <p className="text-xs font-mono text-stone-500 pt-1">Status: {ad.status}{ad.review_note ? ` · “${ad.review_note}”` : ""}</p>} />
        ))}
      </div>
    </div>
  );
}
